import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import TitleField from '../TitleField';
import { Card, Typography, Chip, Table, TableBody, TableRow, TableCell, Divider, Box, Grid } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    width: '100%',
  },

  section: {
    marginTop: theme.spacing(2),
    pageBreakInside: 'avoid',
  },

  label: {
    fontWeight: 500,
    color: '#666',
    fontSize: 13,
  },

  successChip: {
    backgroundColor: '#e6f4ea',
    color: '#1e7e34',
    fontWeight: 600,
  },

  failChip: {
    backgroundColor: '#fdecea',
    color: '#c62828',
    fontWeight: 600,
  },

  gridImage: {
    width: '100%',
    height: 180,
    objectFit: 'contain',
    borderRadius: 6,
    border: '1px solid #ddd',
    background: '#fafafa',
  },
}));

// 🔥 FORMATTER
const formatFieldValue = (fieldType, value) => {
  if (!value) return '-';

  switch (fieldType) {
    case 'Date of birth':
    case 'Date of issue': {
      const parsed = new Date(value);
      return isNaN(parsed.getTime())
        ? value
        : parsed.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          });
    }

    case 'Sex':
      return value === 'M' ? 'Male' : value === 'F' ? 'Female' : value;

    case 'Address':
      return value.split('\n').map((line, i) => (
        <span key={i}>
          {line}
          <br />
        </span>
      ));

    default:
      return value;
  }
};

const RegtankDocumentDownload = ({ documents = {}, docField = {}, docImage = {}, className }) => {
  const classes = useStyles();

  const docInfo = documents?.documentInfo || {};
  const idvV1Info = documents?.idvV1Info || {};
  const extensions = idvV1Info?.documentExtensions || [];
  const fields = idvV1Info?.ocrV2Info?.documentFields || [];

  const hasDocField = Array.isArray(docField) && docField.length > 0;
  const displayFields = useMemo(() => {
    if (!hasDocField) {
      return fields.map((f) => ({
        fieldType: f.fieldType,
        label: f.fieldType,
        value:
          f.visualZone?.trim() ||
          f.barcode?.trim() ||
          f.mrz?.trim() ||
          '-',
        valid: null,
      }));
    }

    return docField.map((df) => {
      const match = fields.find(
        (f) => f.fieldType === df.fieldType
      );

      return {
        fieldType: df.fieldType,
        label: df.fieldType,
        value:
          match?.visualZone?.trim() ||
          match?.barcode?.trim() ||
          match?.mrz?.trim() ||
          '-',
        valid: df.valid,
      };
    });
  }, [docField, fields, hasDocField]);

  const errorFields = fields.filter((field) => field.valid === false);

  const getExt = (key) => extensions.find((e) => e.key === key)?.value;
  const renderImageQuality = (title, data) => {
    if (!data) return null;

    // Only keep values that should actually be displayed
    const validEntries = Object.entries(data).filter(
      ([, value]) =>
        value !== null &&
        value !== undefined &&
        value !== '' &&
        value !== 'WAS_NOT_DONE'
    );

    // Don't show the entire section if there's nothing to display
    if (validEntries.length === 0) return null;

    return (
      <Box className={classes.section}>
        <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
          📷 Image Quality ({title})
        </Typography>

        {validEntries.map(([key, value]) => {
          const isError = value === 'ERROR';

          return (
            <Box
              key={key}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              borderBottom="1px solid #eee"
              py={1}
            >
              <Typography style={{ color: '#666' }}>
                {key.replace(/([A-Z])/g, ' $1')}
              </Typography>

              <Chip
                label={value}
                size="small"
                className={
                  isError
                    ? classes.failChip
                    : classes.successChip
                }
              />
            </Box>
          );
        })}
      </Box>
    );
  };
  return (
    <Card className={clsx(classes.card, className)}>
      <TitleField>ID Photos</TitleField>

      {/* 🔥 2×2 GRID IMAGE LAYOUT */}
      <Box className={classes.section}>
        <Grid container spacing={2}>
          {docInfo.frontDocumentUrl && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Front</Typography>
              <img src={docInfo.frontDocumentUrl} className={classes.gridImage} />
            </Grid>
          )}

          {docInfo.backDocumentUrl && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Back</Typography>
              <img src={docInfo.backDocumentUrl} className={classes.gridImage} />
            </Grid>
          )}

          {getExt('Portrait') && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Portrait</Typography>
              <img src={getExt('Portrait')} className={classes.gridImage} />
            </Grid>
          )}

          {getExt('Barcode') && (
            <Grid item xs={6}>
              <Typography style={{ marginBottom: 6 }}>Barcode</Typography>
              <img src={getExt('Barcode')} className={classes.gridImage} />
            </Grid>
          )}
        </Grid>
      </Box>

      {/* 🔥 IMAGE QUALITY */}
      {renderImageQuality('Front', docImage?.front)}
      {renderImageQuality('Back', docImage?.back)}

      <Divider style={{ margin: '16px 0' }} />
      <TitleField>Verification Summary</TitleField>

      {/* 🔴 ISSUES */}
      {errorFields.length > 0 && (
            <Box className={classes.section}>
              <Typography
                style={{
                  fontWeight: 600,
                  marginBottom: 8,
                  color: '#d32f2f',
                }}
              >
                ⚠️ Issues Found
              </Typography>

              <Table size="small">
                <TableBody>
                  {errorFields.map((f, i) => (
                    <React.Fragment key={i}>
                      {/* Field */}
                      <TableRow style={{ background: '#fff5f5' }}>
                        <TableCell style={{ fontWeight: 600 }}>
                          {f.fieldType}
                        </TableCell>

                        <TableCell />

                        <TableCell align="right">
                          <Chip
                            label="Failed"
                            size="small"
                            style={{
                              backgroundColor: '#fdecea',
                              color: '#d32f2f',
                            }}
                          />
                        </TableCell>
                      </TableRow>

                      {/* MRZ */}
                      <TableRow>
                        <TableCell style={{ paddingLeft: 32 }}>
                          MRZ
                        </TableCell>

                        <TableCell>
                          {f.mrz?.trim() || 'Not detected'}
                        </TableCell>

                        <TableCell align="right">
                          <Chip
                            label={f.mrzStatus === true ? 'Passed' : 'Failed'}
                            size="small"
                            style={
                              f.mrzStatus === true
                                ? {
                                    backgroundColor: '#e6f7ee',
                                    color: '#137a45',
                                  }
                                : {
                                    backgroundColor: '#fdecea',
                                    color: '#d32f2f',
                                  }
                            }
                          />
                        </TableCell>
                      </TableRow>

                      {/* Visual Zone */}
                      <TableRow>
                        <TableCell style={{ paddingLeft: 32 }}>
                          Visual Zone
                        </TableCell>

                        <TableCell>
                          {f.visualZone?.trim() || 'Not detected'}
                        </TableCell>

                        <TableCell align="right">
                          <Chip
                            label={
                              f.visualZoneStatus === true
                                ? 'Passed'
                                : f.visualZoneStatus === false
                                ? 'Failed'
                                : 'Not Checked'
                            }
                            size="small"
                            style={
                              f.visualZoneStatus === true
                                ? {
                                    backgroundColor: '#e6f7ee',
                                    color: '#137a45',
                                  }
                                : f.visualZoneStatus === false
                                ? {
                                    backgroundColor: '#fdecea',
                                    color: '#d32f2f',
                                  }
                                : {
                                    backgroundColor: '#e0e0e0',
                                    color: '#555',
                                  }
                            }
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

      {/* 🔥 ALL FIELDS */}
      <Box className={classes.section}>
        {displayFields.map((f, i) => (
          <Box
            key={i}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            borderBottom="1px solid #eee"
            py={1}
          >
            {/* Label */}
            <Typography
              style={{
                color: '#666',
                width: '35%',
                flexShrink: 0,
              }}
            >
              {f.label}
            </Typography>

            {/* Value */}
            <Typography
              component="div"
              style={{
                fontWeight: 500,
                width: '65%',
                textAlign: 'right',
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
              }}
            >
              {formatFieldValue(f.fieldType, f.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Card>
  );
};

export default RegtankDocumentDownload;
